/* eslint-disable i18next/no-literal-string */
import { cn } from "~/lib/utils";
import { LandingPageHeaderH1 } from "~/components/landing/LandingText";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const shoploanerHeroBackground = imageUrl("/images/shoploaner/shoploaner-hero-background.jpg");
const shoploanerHeroBackgroundPortrait =
  imageUrl("/images/shoploaner/shoploaner-hero-background-portrait.jpg");
const shopLoanerLogo = imageUrl("/images/shoploaner/shop-loaner-logo.png");

/**
 * Ported from bouncieweb's `LandingPageComponents/LoanerHero.js`.
 *
 * A rounded hero card with the ShopLoaner logo and a left-aligned headline. The
 * `#7b8281` fill behind the image is the photo's average color — it smooths over
 * the load delay, so it is preserved.
 *
 * Same shape as TuroHero with two differences: two background crops (portrait
 * below sm, landscape from sm) rather than one, and no top margin at base — the
 * original only sets marginTop from sm up.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  sm 640px -> sm:  md 768px -> md:
 *   lg 1024px -> lg:  xlg 1280px -> xl:
 */

export default function LoanerHero() {
  return (
    // HeroWrapper
    <div className="mb-12 flex justify-center overflow-hidden sm:mx-[50px] sm:mt-8 md:mb-20">
      {/* HeroRectangle */}
      <div
        className={cn(
          "relative h-[454px] w-full overflow-hidden text-white",
          "bg-[#7b8281]",
          "sm:h-[400px] sm:rounded-[20px] xl:max-w-[1400px]",
        )}
      >
        {/* HeroBgImagePortrait — hidden from sm up */}
        <Picture
          src={shoploanerHeroBackgroundPortrait}
          loading="eager"
          alt="Person holding a car key with a blurred parking lot full of cars in the background"
          className={cn(
            "absolute h-full w-full max-w-none object-cover",
            "object-[center_center] min-[431px]:object-[bottom_center]",
            "sm:hidden",
          )}
        />
        {/* HeroBgImageLandscape — hidden below sm */}
        <Picture
          src={shoploanerHeroBackground}
          loading="eager"
          alt="Person holding a car key with a blurred parking lot full of cars in the background"
          className={cn(
            "absolute hidden h-full w-full max-w-none object-cover",
            "sm:block sm:object-[60%_center] md:object-[60%_0%] lg:object-[center_37%]",
          )}
        />

        {/* Dimmer — mobile-only scrim */}
        <div className="absolute top-0 left-0 h-full w-full bg-black/[0.21] md:hidden" />

        {/* ContentArea */}
        <div className="relative mt-8 mr-[34px] ml-[34px] md:mt-[42px] md:mr-[85px] md:ml-[85px] lg:mt-[52px] lg:mr-[140px] lg:ml-[140px]">
          {/* ShopLoaner logo */}
          <Picture
            src={shopLoanerLogo}
            loading="eager"
            alt="ShopLoaner Logo"
            className="h-fit w-full max-w-none min-[431px]:w-[306px]"
          />

          {/* HeroHeader (LandingPageHeaderH1 + hero overrides) */}
          <LandingPageHeaderH1
            className={cn(
              "mt-[23px] mb-6 max-w-[550px] text-left [text-wrap:wrap]",
              "text-[40px] leading-[48px]",
              "min-[431px]:text-[40px] sm:mt-[30px] sm:text-[48px] sm:leading-[56px]",
            )}
            style={{ textShadow: "0px 4px 13px #00000030" }}
          >
            Safeguard your loaner fleet with Bouncie!
          </LandingPageHeaderH1>

          {/* HeroSubHeader */}
          <div className="text-[20px] sm:text-[24px]">
            Loaner fleet visibility. Simplified.
          </div>
        </div>
      </div>
    </div>
  );
}
